import classNames from "classnames";
import ApplicationHeader from "./application_header";
import type Application from "@/types/application";
import type { GhrTask } from "@/types/task";
import { Stack, Row } from "@sprout/layout";
import { Tag } from "@sprout/tag";
import Divider from "@sprout/divider";
import { SecondaryBody, SectionTitle } from "@sprout/typography";

import "./selected_application.scss";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState, Suspense } from "react";
import Tooltip from "@/sprout/tooltip";
import ErrorIcon from "@/sprout/icons/error";
import { SecondaryButton } from "@sprout/buttons";
import Stages from "@components/applications/stages.tsx";
import ActivityHistory from "@components/applications/activity_history.tsx";
import useTasks from "@/hooks/use_tasks";
import GHR_TASK_CONFIG from "@/config/ghr_task_config";
import ApplicationTaskBanner from "./application_task_banner/application_task_banner";
import { ErrorBoundary } from "react-error-boundary";
import { useTimeAgo, composeStatusLabel } from "@utils/time_ago";
import CurrentStageCard from "./current_stage_card";
import { stageProgressFor } from "./stage_progress";

const ApplicationTaskSection = ({ applicationId }: { applicationId: number }) => {
  const { data: tasks } = useTasks();

  const oldestApplicationTask = useMemo(
    () =>
      tasks
        .filter(
          (task): task is GhrTask =>
            (task as GhrTask).application_id === applicationId &&
            (task as GhrTask).taskable_type in GHR_TASK_CONFIG,
        )
        .sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())[0],
    [tasks, applicationId],
  );

  if (!oldestApplicationTask) return null;
  return <ApplicationTaskBanner task={oldestApplicationTask} />;
};

type Props = {
  application: Application;
  canChooseDreamJob: boolean;
  onViewParentApplication: (id: number) => void;
};

const SelectedApplication = ({
  application,
  canChooseDreamJob,
  onViewParentApplication,
}: Props) => {
  const { t } = useTranslation("applications", { keyPrefix: "dream_job_callout" });
  const { t: tApplications } = useTranslation("applications");

  const [currentApplication, setCurrentApplication] = useState(application);
  const appliedLabel = composeStatusLabel(
    tApplications("applied"),
    useTimeAgo(currentApplication.applied_at),
  );

  useEffect(() => {
    setCurrentApplication(application);
  }, [application]);

  const onAddDreamJob = async () => {
    setCurrentApplication({ ...currentApplication, dream_job: true });
  };

  const handleViewFirstApplication = () => {
    if (currentApplication.duplicate_of_application_id) {
      onViewParentApplication(currentApplication.duplicate_of_application_id);
    }
  };

  const stageProgress = stageProgressFor(currentApplication);

  const jobPost = (
    <div
      className="job-post-contents"
      dangerouslySetInnerHTML={{ __html: currentApplication.description }}
    />
  );

  return (
    <Stack gap="16px">
      <ApplicationHeader
        application={currentApplication}
        canChooseDreamJob={canChooseDreamJob}
        onAddDreamJob={onAddDreamJob}
      />
      <Stack gap="24px">
        <Row gap="8px" align="center">
          {currentApplication.locations && (
            <Tooltip text={currentApplication.locations}>
              <Tag label={currentApplication.locations} maxWidth="max-w-52" />
            </Tooltip>
          )}
          {/* Salary is out of scope */}
          <SecondaryBody>{appliedLabel}</SecondaryBody>
        </Row>
        {!currentApplication.inactive && (
          <ErrorBoundary fallback={null}>
            <Suspense fallback={null}>
              <ApplicationTaskSection applicationId={currentApplication.id} />
            </Suspense>
          </ErrorBoundary>
        )}
        {currentApplication.inactive && currentApplication.duplicate && (
          <Row gap="8px" align="center" className="duplicate-callout">
            <div className="w-full bg-marigold-25 px-6 py-4 border-marigold-e50 rounded-sm">
              <div className="flex flex-col gap-4 xl:flex-row items-start">
                <Stack gap="0">
                  <Row gap="4px" align="center">
                    <ErrorIcon size="md" color="light-yellow" />
                    <p className="text-sm w-full">{t("duplicate_application.description")}</p>
                  </Row>
                </Stack>

                {currentApplication.duplicate_of_application_id && (
                  <SecondaryButton size="medium" onClick={handleViewFirstApplication}>
                    {t("duplicate_application.button")}
                  </SecondaryButton>
                )}
              </div>
            </div>
          </Row>
        )}
        <Stack gap="24px">
          <Divider />
          <div
            className={classNames("application--content", {
              "application--content--with-stage": stageProgress,
            })}
          >
            {stageProgress && (
              <div className="application--stage">
                <CurrentStageCard {...stageProgress} />
              </div>
            )}
            <div className="application--job-post">
              <Stack gap="8px" alignItems="stretch">
                <SectionTitle>{tApplications("job_post")}</SectionTitle>
                <div className="border-1 border-gray-700 rounded-[8px] px-6 py-4">{jobPost}</div>
              </Stack>
            </div>
            <Stack gap="24px" className="application--sidebar">
              <Stages application={currentApplication} />
              <ActivityHistory activityHistory={currentApplication.activityHistory ?? []} />
            </Stack>
          </div>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SelectedApplication;
